import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { Plus, Loader2, UserPlus, Trash2, Crown, Shield, User } from 'lucide-react';
import { ConfirmDialog } from './ConfirmDialog';
import {
  OrganizationDetail,
  OrganizationMember,
  listOrgMembers,
  addOrgMember,
  updateOrgMemberRole,
  removeOrgMember,
  ORG_ROLES,
} from '@/services/orgTeamApi';

interface MembersTabProps {
  organization: OrganizationDetail;
  currentUserId: string;
  userRole: string;
  onUpdate: () => void;
}

export function MembersTab({
  organization,
  currentUserId,
  userRole,
  onUpdate,
}: MembersTabProps) {
  const { toast } = useToast();

  const isOwner = userRole === 'owner';
  const isAdmin = userRole === 'admin' || isOwner;

  const [isLoading, setIsLoading] = useState(true);
  const [members, setMembers] = useState<OrganizationMember[]>([]);

  // Add member dialog state
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addData, setAddData] = useState({ email: '', role: 'member' });
  const [isAdding, setIsAdding] = useState(false);

  // Role change state
  const [changingRoleFor, setChangingRoleFor] = useState<string | null>(null);

  // Remove member state
  const [removingMember, setRemovingMember] = useState<OrganizationMember | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  // Load members
  const loadMembers = useCallback(async () => {
    try {
      const data = await listOrgMembers(organization.id);
      setMembers(data);
    } catch (error) {
      console.error('Failed to load members:', error);
      toast({
        title: 'Error',
        description: 'Failed to load members',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [organization.id, toast]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  // Handle add member
  const handleAddMember = async () => {
    if (!addData.email.trim()) {
      toast({
        title: 'Error',
        description: 'Email is required',
        variant: 'destructive',
      });
      return;
    }

    setIsAdding(true);
    try {
      await addOrgMember(organization.id, {
        email: addData.email.trim(),
        role: addData.role,
      });

      toast({
        title: 'Member added',
        description: `${addData.email} has been added to the organization`,
      });

      setShowAddDialog(false);
      setAddData({ email: '', role: 'member' });
      loadMembers();
      onUpdate();
    } catch (error: any) {
      console.error('Failed to add member:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to add member',
        variant: 'destructive',
      });
    } finally {
      setIsAdding(false);
    }
  };

  // Handle role change
  const handleRoleChange = async (userId: string, newRole: string) => {
    setChangingRoleFor(userId);
    try {
      await updateOrgMemberRole(organization.id, userId, newRole);

      toast({
        title: 'Role updated',
        description: 'Member role has been updated',
      });

      loadMembers();
    } catch (error: any) {
      console.error('Failed to update role:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update role',
        variant: 'destructive',
      });
    } finally {
      setChangingRoleFor(null);
    }
  };

  // Handle remove member
  const handleRemoveMember = async () => {
    if (!removingMember) return;

    setIsRemoving(true);
    try {
      await removeOrgMember(organization.id, removingMember.user_id);

      toast({
        title: 'Member removed',
        description: `${removingMember.display_name || removingMember.username} has been removed`,
      });

      setRemovingMember(null);
      loadMembers();
      onUpdate();
    } catch (error: any) {
      console.error('Failed to remove member:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to remove member',
        variant: 'destructive',
      });
    } finally {
      setIsRemoving(false);
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'owner':
        return <Crown className="h-3 w-3" />;
      case 'admin':
        return <Shield className="h-3 w-3" />;
      default:
        return <User className="h-3 w-3" />;
    }
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'owner':
        return 'default';
      case 'admin':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString();
  };

  const canChangeRole = (member: OrganizationMember) => {
    // Only owners can change roles
    if (!isOwner) return false;
    // Can't change your own role
    if (member.user_id === currentUserId) return false;
    return true;
  };

  const canRemoveMember = (member: OrganizationMember) => {
    // Admins can remove non-owners
    if (isAdmin && member.role !== 'owner') return true;
    // Can remove yourself
    if (member.user_id === currentUserId) return true;
    return false;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Members</CardTitle>
            <CardDescription>
              Manage organization members and their roles
            </CardDescription>
          </div>
          {isAdmin && (
            <Button onClick={() => setShowAddDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Member
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : members.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <UserPlus className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No members</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Member</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead className="w-24">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map((member) => (
                <TableRow key={member.user_id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium">
                        {(member.display_name || member.username).slice(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium">{member.display_name || member.username}</p>
                        <p className="text-xs text-muted-foreground">@{member.username}</p>
                      </div>
                      {member.user_id === currentUserId && (
                        <Badge variant="outline" className="text-xs">You</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {member.email || '-'}
                  </TableCell>
                  <TableCell>
                    {canChangeRole(member) ? (
                      <Select
                        value={member.role}
                        onValueChange={(value) => handleRoleChange(member.user_id, value)}
                        disabled={changingRoleFor === member.user_id}
                      >
                        <SelectTrigger className="w-[120px]">
                          {changingRoleFor === member.user_id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <SelectValue />
                          )}
                        </SelectTrigger>
                        <SelectContent>
                          {ORG_ROLES.map((role) => (
                            <SelectItem key={role.value} value={role.value}>
                              <div className="flex items-center gap-2">
                                {getRoleIcon(role.value)}
                                {role.label}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge
                        variant={getRoleBadgeVariant(member.role) as any}
                        className="capitalize gap-1"
                      >
                        {getRoleIcon(member.role)}
                        {member.role}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>{formatDate(member.joined_at)}</TableCell>
                  <TableCell>
                    {canRemoveMember(member) && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setRemovingMember(member)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Add Member Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Member</DialogTitle>
            <DialogDescription>
              Add an existing user to {organization.name} by their email address
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="member-email">Email Address</Label>
              <Input
                id="member-email"
                type="email"
                value={addData.email}
                onChange={(e) => setAddData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="user@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="member-role">Role</Label>
              <Select
                value={addData.role}
                onValueChange={(value) => setAddData(prev => ({ ...prev, role: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ORG_ROLES.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      <div>
                        <p className="font-medium">{role.label}</p>
                        <p className="text-xs text-muted-foreground">{role.description}</p>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)} disabled={isAdding}>
              Cancel
            </Button>
            <Button onClick={handleAddMember} disabled={isAdding}>
              {isAdding && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add Member
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Member Confirmation Dialog */}
      <ConfirmDialog
        open={!!removingMember}
        onOpenChange={(open) => !open && setRemovingMember(null)}
        title="Remove Member"
        description={
          removingMember?.user_id === currentUserId
            ? 'Are you sure you want to leave this organization? You will lose access to all organization resources.'
            : `Are you sure you want to remove "${removingMember?.display_name || removingMember?.username}" from this organization?`
        }
        confirmLabel={removingMember?.user_id === currentUserId ? 'Leave' : 'Remove'}
        variant="destructive"
        isLoading={isRemoving}
        onConfirm={handleRemoveMember}
      />
    </Card>
  );
}
